import { useState, type InputHTMLAttributes, type ReactNode } from "react";

import { EyeIcon, EyeOffIcon } from "./icons";

export interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
  /** Optional leading glyph (e.g. a lock icon). */
  icon?: ReactNode;
  /** Accessible label for the reveal button while the password is hidden. */
  revealLabel?: string;
  /** Accessible label for the reveal button while the password is shown. */
  hideLabel?: string;
}

/** A password input with a show/hide reveal toggle — mirrors the Jmix login password field. Reuses
 *  the shared `.fui-field` vocabulary (optional leading icon) plus a trailing reveal button. */
export function PasswordField({
  label,
  icon,
  revealLabel = "Show password",
  hideLabel = "Hide password",
  ...rest
}: PasswordFieldProps) {
  const [revealed, setRevealed] = useState(false);
  return (
    <label className="fui-field">
      <span className="fui-field__label">{label}</span>
      <span className="fui-field__control">
        {icon ? (
          <span className="fui-field__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <input
          className={
            "fui-field__input fui-field__input--with-trailing" +
            (icon ? " fui-field__input--with-icon" : "")
          }
          type={revealed ? "text" : "password"}
          {...rest}
        />
        <button
          type="button"
          className="fui-field__reveal"
          aria-label={revealed ? hideLabel : revealLabel}
          aria-pressed={revealed}
          onClick={() => setRevealed((v) => !v)}
        >
          {revealed ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </span>
    </label>
  );
}
